import { Avatar, Link } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AddItemIcon } from 'components/icons';
import SpaceIcon from 'components/SpaceIcon';
import { orderBy } from 'natural-orderby';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { graphql, useFragment } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import {
  generatePath,
  ORG_SETTINGS_PARAM_NAME,
  SPACE_PATH,
  TENNANT_SETTINGS_SPACES_PATH_PREFIX,
} from 'utils/routes';
import { MenuLink } from '.';
import { useDesktopMenu } from './DesktopMenuContext';
import { MenuItemStack } from './MenuItemStack';
import { MenuSubtitle } from './MenuSubtitle';
import { SpacesMenuSection_spaces$key } from './__generated__/SpacesMenuSection_spaces.graphql';
import { SubtitleIconButton } from 'apps/TenantApp/screens/Start';

const fragment = graphql`
  fragment SpacesMenuSection_spaces on SpaceMembershipConnection {
    edges {
      node {
        space {
          __typename
          id
          order
          icon {
            ... on NamedIcon {
              name
            }
          }
          name
          color {
            light
          }
        }
      }
    }
  }
`;

interface SpacesMenuSectionProps {
  spaces?: SpacesMenuSection_spaces$key | null;
  canCreateSpace: boolean;
}

export const SpacesMenuSection = ({
  spaces,
  canCreateSpace,
}: SpacesMenuSectionProps) => {
  return (
    <MenuItemStack aria-label="Space Navigation">
      <MenuSubtitle
        rightButton={
          <SubtitleIconButton
            active={canCreateSpace}
            as={Link}
            to={`?${ORG_SETTINGS_PARAM_NAME}=${TENNANT_SETTINGS_SPACES_PATH_PREFIX}`}
            aria-label={t`Add a space`}
            icon={<AddItemIcon />}
          />
        }
      >
        {t`Spaces`}
      </MenuSubtitle>
      {spaces ? (
        <ListOfSpaceMenuLinks spaces={spaces} />
      ) : (
        !canCreateSpace && <NoSpaceLabel />
      )}
    </MenuItemStack>
  );
};

interface ListOfSpaceMenuLinksProps {
  spaces: SpacesMenuSection_spaces$key;
}

export const ListOfSpaceMenuLinks = ({
  spaces: _spaces,
}: ListOfSpaceMenuLinksProps) => {
  const { edges } = useFragment(fragment, _spaces);
  const spaces = edges.map((edge) => edge?.node?.space).filter(notEmpty) || [];
  const sortedSpaces = orderBy(spaces, [(s) => s.order, 'asc']);
  const { isCollapsed } = useDesktopMenu();
  const location = useLocation();
  return (
    <>
      {sortedSpaces.map((space) => {
        const spacePath = generatePath(SPACE_PATH, { spaceId: space.id });
        const isActive = location.pathname.includes(spacePath);
        return (
          <MenuLink
            key={space.id}
            to={spacePath}
            icon={
              isCollapsed ? (
                <Avatar size="sm" bg={space.color.light} name={space.name} />
              ) : (
                <SpaceIcon name={space.icon?.name} color={space.color.light} />
              )
            }
            label={space.name}
            isActive={isActive}
          />
        );
      })}
    </>
  );
};

const NoSpaceLabel = () => {
  return <MenuLink to="." isDisabled icon={null} label={t`No spaces`} />;
};
